import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #F8FCFF;
`;

const FormContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  gap: 24px;
  width: 391px;
  border: 1px solid #E2E2E3;
  border-radius: 24px;
  background-color: white;
`;

const Logo = styled.img`
  width: 60px;
  height: 60px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  display: flex;
  align-items: center;
`;

const RequiredStar = styled.span`
  color: #E43434;
  margin-right: 4px;
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 343px;
  height: 56px;
  padding: 18px 16px;
  background: #FFFFFF;
  border: 1px solid ${props => props.error ? '#E43434' : '#A7A7AA'};
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }

  &:focus {
    outline: none;
    border-color: #2575BD;
  }
`;

const HelperText = styled.span`
  width: 343px;
  height: 17px;
  font-family: 'Sofia Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #E43434;
`;

const Button = styled.button`
  width: 180px;
  height: 54px;
  background-color: ${props => props.disabled ? '#E0F0FF' : '#2575BD'};
  color: ${props => props.disabled ? '#91D3FF' : '#FFFFFF'};
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 18px;
  line-height: 21px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s;
  margin: 0 auto;
  display: block;

  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const SignUpLink = styled.p`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 14px;
  line-height: 17px;
  color: #6D6D71;
  margin-top: 16px;
  text-align: center;

  a {
    color: #2575BD;
    text-decoration: none;
    margin-left: 4px;
    cursor: pointer;
  }
`;

const SignInPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(
      formData.email && 
      formData.password && 
      Object.keys(errors).filter(key => key !== 'submit').length === 0
    );
  }, [formData, errors]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    validateField(e.target.name, e.target.value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };

    switch (name) {
      case 'email':
        if (!value) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          newErrors.email = 'Email is invalid';
        } else {
          delete newErrors.email;
        }
        break;
      case 'password':
        if (!value) {
          newErrors.password = 'Password is required';
        } else {
          delete newErrors.password;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormValid) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}user/login`, formData);
        const token = response.data.token;
        localStorage.setItem('token', token);
        setIsAuthenticated(true);
        navigate('/listing');
      } catch (error) {
        console.error('Signin error:', error);
        if (error.response && error.response.data && error.response.data.error) {
          setErrors(prevErrors => ({ ...prevErrors, submit: error.response.data.error }));
        } else {
          setErrors(prevErrors => ({ ...prevErrors, submit: 'An unexpected error occurred. Please try again.' }));
        }
      }
    }
  };

  const handleSignUpClick = () => {
    navigate('/signup');
  };

  return (
    <PageContainer>
      <FormContainer>
        <Logo src="/heart_logo.png" alt="Heart Logo" />
        <Form onSubmit={handleSubmit}>
          <InputWrapper>
            <Label><RequiredStar>*</RequiredStar>Email</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              error={!!errors.email}
            />
            {errors.email && <HelperText>{errors.email}</HelperText>}
          </InputWrapper>
          <InputWrapper>
            <Label><RequiredStar>*</RequiredStar>Password</Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              error={!!errors.password}
            />
            {errors.password && <HelperText>{errors.password}</HelperText>}
          </InputWrapper>
          {errors.submit && <HelperText>{errors.submit}</HelperText>}
          <Button type="submit" disabled={!isFormValid}>
            Sign In
          </Button>
        </Form>
        <SignUpLink>
          New User?
          <a onClick={handleSignUpClick}>Sign Up</a>
        </SignUpLink>
      </FormContainer>
    </PageContainer>
  );
};

export default SignInPage;
